<script setup lang="ts">
  // Opened via showDynamicDialog<ITableDiscountModalResult | null>(...,
  // 'pos-table-discount') from use-table-discount.ts.
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { IOrder } from '@/interfaces/IOrder';
  import { computeTableDiscountPreview } from '@/domain/pos/table-discount-derivation';
  import { formatTableDisplayName } from '@/domain/pos/order-formatting';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  export type ITableDiscountModalResult =
    | { action: 'apply'; isPercentage: boolean; value: number }
    | { action: 'remove' };

  const props = withDefaults(
    defineProps<{
      tableName?: string | null;
      tableOrders: IOrder[];
      openSessionId: string;
      initial?: { isPercentage: boolean; value: number } | null;
      hasPosOwnedOrders?: boolean;
      paidMoneyPresent?: boolean;
      uncoveredEligibleCount?: number;
    }>(),
    {
      tableName: null,
      initial: null,
      hasPosOwnedOrders: false,
      paidMoneyPresent: false,
      uncoveredEligibleCount: 0,
    }
  );

  const modals = useModal();
  const money = usePosMoney();
  const restaurantStore = useRestaurantStore();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isPercentage = ref(props.initial?.isPercentage ?? true);
  const inputValue = ref(
    props.initial != null ? String(props.initial.value) : ''
  );

  /** Mirrors the server validation: % in (0,100]; fixed > 0. */
  const parsedValue = computed<number | null>(() => {
    const parsed = Number.parseFloat(inputValue.value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    if (isPercentage.value && parsed > 100) return null;
    return parsed;
  });

  const suffix = computed(() =>
    isPercentage.value ? '%' : restaurantStore.currency?.symbol ?? '$'
  );

  const preview = computed(() => {
    if (parsedValue.value == null) return null;
    return computeTableDiscountPreview(props.tableOrders, props.openSessionId, {
      isPercentage: isPercentage.value,
      value: parsedValue.value,
    });
  });

  const canApply = computed(
    () => parsedValue.value != null && !props.hasPosOwnedOrders
  );

  // Deduped like the covers modal ("Mesa 5", never "mesa Mesa 5"); the name
  // should always be present, so the bare table word is defensive only.
  const title = computed(() => {
    const tableWord = i18nT('posV2.tableWord');
    const table = props.tableName
      ? formatTableDisplayName(props.tableName, tableWord)
      : tableWord;
    return i18nT('posV2.tableDiscountTitle', { table });
  });

  // PosUiInput has no autofocus support: focus its inner input by hand.
  const fieldRef = ref<HTMLElement>();
  onMounted(() => {
    fieldRef.value?.querySelector('input')?.focus();
  });

  function setMode(percent: boolean) {
    isPercentage.value = percent;
  }

  function submit(result: ITableDiscountModalResult | null) {
    modals.dialogSubmit('pos-table-discount', result);
  }

  function apply() {
    if (!canApply.value || parsedValue.value == null) return;
    submit({
      action: 'apply',
      isPercentage: isPercentage.value,
      value: parsedValue.value,
    });
  }
</script>

<template>
  <div class="pos-v2-table-discount-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <span class="modal-subtitle">{{
        $t('posV2.tableDiscountSubtitle')
      }}</span>
    </div>

    <div class="discount-body">
      <div class="mode-switch">
        <button
          type="button"
          class="mode-option"
          :class="{ 'is-active': isPercentage }"
          @click="setMode(true)"
        >
          {{ $t('posV2.tableDiscountPercent') }}
        </button>
        <button
          type="button"
          class="mode-option"
          :class="{ 'is-active': !isPercentage }"
          @click="setMode(false)"
        >
          {{ $t('posV2.tableDiscountFixed') }}
        </button>
      </div>

      <div ref="fieldRef" class="amount-field">
        <PosUiInput
          :value="inputValue"
          type="number"
          inputmode="decimal"
          size="lg"
          icon="tag-outline"
          class="amount-input"
          @input="(v) => (inputValue = v)"
          @enter="apply"
        />
        <span class="amount-suffix">{{ suffix }}</span>
      </div>

      <div v-if="preview" class="preview-line">
        {{
          $t('posV2.tableDiscountPreview', {
            from: money.formatComputedMoney(preview.from),
            to: money.formatComputedMoney(preview.to),
          })
        }}
      </div>

      <div v-if="paidMoneyPresent" class="note note--warning">
        <PosUiIcon name="alert-outline" class="note-icon" />
        <span>{{ $t('posV2.tableDiscountPaidWarning') }}</span>
      </div>

      <div v-if="hasPosOwnedOrders" class="note note--error">
        <PosUiIcon name="alert-circle-outline" class="note-icon" />
        <span>{{ $t('posV2.tableDiscountPosBlocked') }}</span>
      </div>

      <div v-if="uncoveredEligibleCount > 0 && initial" class="note note--info">
        <PosUiIcon name="information-outline" class="note-icon" />
        <span>
          {{
            $tc('posV2.tableDiscountUncovered', uncoveredEligibleCount, {
              count: uncoveredEligibleCount,
            })
          }}
        </span>
      </div>
    </div>

    <div class="modal-footer">
      <PosUiButton
        v-if="initial != null"
        color="error"
        variant="soft"
        size="lg"
        class="btn-remove"
        @click="submit({ action: 'remove' })"
      >
        {{ $t('posV2.tableDiscountRemove') }}
      </PosUiButton>
      <div class="footer-spacer" />
      <PosUiButton
        color="neutral"
        variant="ghost"
        size="lg"
        class="btn-cancel"
        @click="submit(null)"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        variant="solid"
        size="lg"
        class="btn-apply"
        :disabled="!canApply"
        @click="apply"
      >
        {{ $t('posV2.tableDiscountApply') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-table-discount-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .discount-body {
      @apply tw-flex tw-flex-col tw-gap-3;
    }

    .mode-switch {
      @apply tw-grid tw-grid-cols-2 tw-gap-0.5 tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

      .mode-option {
        @apply tw-min-h-[44px] tw-rounded-md tw-border tw-border-transparent tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

        &.is-active {
          @apply tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
        }
      }
    }

    .amount-field {
      @apply tw-relative;

      .amount-input {
        :deep(.pos-ui-input-field) {
          @apply tw-pr-10 tw-text-lg tw-font-semibold;
        }
      }

      .amount-suffix {
        @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-3 tw-flex tw-items-center tw-text-base tw-font-semibold tw-text-neutral-400;
      }
    }

    .preview-line {
      @apply tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-neutral-700;
    }

    .note {
      @apply tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm;

      .note-icon {
        @apply tw-mt-0.5 tw-flex-shrink-0 tw-text-base;
      }

      &--warning {
        @apply tw-bg-amber-50 tw-text-amber-800;
      }

      &--error {
        @apply tw-bg-red-50 tw-text-red-700;
      }

      &--info {
        @apply tw-bg-sky-50 tw-text-sky-800;
      }
    }

    .modal-footer {
      @apply tw-flex tw-w-full tw-items-center tw-gap-2;

      .footer-spacer {
        @apply tw-flex-1;
      }

      :deep(.btn-apply) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
